import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Code, Loader2, Mail, Paintbrush, Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import {
  EMAIL_TEMPLATE_STYLE_DEFAULTS,
  EMAIL_TEMPLATE_STYLE_FIELDS,
  EMAIL_TEMPLATE_TYPES,
  EmailTemplatesApi,
  type EmailTemplate,
  type EmailTemplateInput,
  type EmailTemplateStyleField,
  type EmailTemplateType,
} from '@/services/networks/emailTemplatesApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { DEFAULT_EMAIL_TEMPLATE } from '@/data/defaultEmailTemplate'
import { formatDate } from '@/utils/format'
import { AppShell } from '../../components/AppShell'
import { CodeEditor } from '../../components/CodeEditor'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { SearchSelect } from '../../components/SearchSelect'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { SwitchField } from '../../components/SwitchField'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useNetwork } from '../../contexts/NetworkContext'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import {
  ObjectStateToggle,
  useSaveObjectState,
  type ObjectState,
} from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

/** A template of its own, rather than the API's default structure. */
function isCustomTemplate(template: string) {
  return template.trim() !== '' && template.trim() !== DEFAULT_EMAIL_TEMPLATE.trim()
}

// ── Inner form component ────────────────────────────────────────────────────

interface EmailTemplateEditFormProps {
  emailTemplate: EmailTemplate | null
  id: string
  isNew: boolean
}

function EmailTemplateEditForm({ emailTemplate, id, isNew }: EmailTemplateEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const customTemplate = isCustomTemplate(emailTemplate?.template ?? '')

  const form = useForm({
    defaultValues: {
      name: emailTemplate?.name ?? '',
      description: emailTemplate?.description ?? '',
      type: (emailTemplate?.type ?? 'SPECIFIC') as EmailTemplateType,
      teamId: emailTemplate?.team?.id ?? '',
      styles: Object.fromEntries(
        EMAIL_TEMPLATE_STYLE_FIELDS.map((field) => [
          field,
          emailTemplate ? emailTemplate[field] : EMAIL_TEMPLATE_STYLE_DEFAULTS[field],
        ])
      ) as Record<EmailTemplateStyleField, string>,
      customTemplate,
      template: customTemplate ? emailTemplate!.template : DEFAULT_EMAIL_TEMPLATE,
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        description: value.description,
        type: value.type,
        ...value.styles,
        // Empty leaves the email in the API's default structure.
        template: value.customTemplate && isCustomTemplate(value.template) ? value.template : '',
        ...(value.teamId || emailTemplate?.team
          ? { team: value.teamId ? { id: value.teamId } : null }
          : {}),
        objectState: (emailTemplate?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => EmailTemplatesApi.update(id, { name: emailTemplate?.name ?? '', objectState }),
    ['email-template', id],
    ['email-templates']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)
  const values = useStore(form.store, (s) => s.values)

  // Teams own templates on a marketplace; elsewhere a team is only kept if one is already set.
  const showTeam = !!currentNetwork?.marketplacePlatform || !!emailTemplate?.team
  const { data: teams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ['teams', ''],
    queryFn: () => TeamsApi.getAll(),
    enabled: showTeam,
    staleTime: 5 * 60 * 1000,
  })

  const mutation = useMutation({
    mutationFn: (input: EmailTemplateInput) =>
      isNew ? EmailTemplatesApi.create(id, input) : EmailTemplatesApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['email-template', id], data)
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      markClean()
      if (isNew) navigate(`/admin/email-templates/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('emailTemplates.createdSuccess') : t('emailTemplates.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('emailTemplates.createFailed')
            : t('emailTemplates.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('emailTemplates.title'), href: '/admin/email-templates' },
    {
      label: isNew
        ? t('emailTemplates.newEmailTemplate')
        : (emailTemplate?.name ?? t('emailTemplates.editTitle')),
    },
  ]

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={emailTemplate?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.emailTemplate')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('emailTemplates.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={emailTemplate!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {emailTemplate && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={emailTemplate.id} /> },
              { label: t('common.type'), content: t(`emailTemplates.types.${emailTemplate.type}`) },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(emailTemplate.createTime)}
                    updated={formatDate(emailTemplate.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="flex flex-col gap-3.5 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-3.5">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Mail} />
                  {t('emailTemplates.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <FieldLabel>{t('common.description')}</FieldLabel>
                    <form.Field name="description">
                      {(field) => (
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('emailTemplates.descriptionPlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('emailTemplates.typeHelp')}>
                      {t('common.type')}
                    </FieldLabel>
                    <form.Field name="type">
                      {(field) => (
                        <Select
                          label={t('common.type')}
                          value={field.state.value}
                          onValueChange={(v) => field.handleChange(v as EmailTemplateType)}
                        >
                          <SelectTrigger>
                            <SelectValue>
                              {t(`emailTemplates.types.${field.state.value}`)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {EMAIL_TEMPLATE_TYPES.map((v) => (
                              <SelectItem key={v} value={v}>
                                {t(`emailTemplates.types.${v}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                  {showTeam && (
                    <div className="p-field">
                      <FieldLabel tooltip={t('emailTemplates.teamHelp')}>
                        {t('emailTemplates.team')}
                      </FieldLabel>
                      <form.Field name="teamId">
                        {(field) => (
                          <SearchSelect
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            options={teams.map((team) => ({ value: team.id, label: team.name }))}
                            placeholder={t('emailTemplates.noTeam')}
                            fallbackLabel={emailTemplate?.team?.name}
                            clearable
                            loading={teamsLoading}
                          />
                        )}
                      </form.Field>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Paintbrush} />
                  {t('emailTemplates.styles')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-3 text-xs text-muted-foreground">
                  {t('emailTemplates.stylesHelp')}
                </p>
                <div className="field-grid">
                  {EMAIL_TEMPLATE_STYLE_FIELDS.map((styleField) => (
                    <div key={styleField} className="p-field">
                      <FieldLabel>{t(`emailTemplates.styleFields.${styleField}`)}</FieldLabel>
                      <form.Field name={`styles.${styleField}`}>
                        {(field) => (
                          <Input
                            className="font-mono text-xs"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={EMAIL_TEMPLATE_STYLE_DEFAULTS[styleField]}
                          />
                        )}
                      </form.Field>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Code} />
                  {t('emailTemplates.template')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3">
                  <SwitchField
                    label={t('emailTemplates.customTemplate')}
                    description={t('emailTemplates.customTemplateHelp', {
                      heading: '{{{heading}}}',
                      body: '{{{body}}}',
                      footer: '{{{footer}}}',
                    })}
                    checked={values.customTemplate}
                    onCheckedChange={(checked) => form.setFieldValue('customTemplate', checked)}
                  />
                  {values.customTemplate ? (
                    <form.Field name="template">
                      {(field) => (
                        <CodeEditor
                          value={field.state.value}
                          onChange={(value) => field.handleChange(value)}
                          variables="none"
                          minHeight="320px"
                        />
                      )}
                    </form.Field>
                  ) : (
                    <CodeEditor
                      value={DEFAULT_EMAIL_TEMPLATE}
                      onChange={() => {}}
                      variables="none"
                      display
                      maxHeight="480px"
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/email-templates')}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('emailTemplates.newEmailTemplate') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function EmailTemplateEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: emailTemplate,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['email-template', id],
    queryFn: () => EmailTemplatesApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('emailTemplates.title'), href: '/admin/email-templates' },
    { label: isNew ? t('emailTemplates.newEmailTemplate') : t('emailTemplates.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return (
    <EmailTemplateEditForm key={id} emailTemplate={emailTemplate ?? null} id={id!} isNew={isNew} />
  )
}
